/**
 * Анализ задержек (ПЛ-22): на сколько окончание ушло от выбранного базового плана и вклад каждой работы
 * методом «если бы не»; окна — сдвиг окончания между соседними базовыми планами.
 */
import type { ScheduleView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';
import { fmtMoment } from './model';

interface Delays {
  baseline: { id: string; name: string; finish: string | null };
  finishBaseline: string | null;
  finishCurrent: string;
  totalDays: number;
  items: Array<{ id: string; code: string; name: string; reasons: string[]; impactDays: number }>;
  windows: Array<{
    from: string;
    to: string;
    fromFinish: string | null;
    toFinish: string | null;
    shiftDays: number;
  }>;
  analysed: number;
}
const n = (x: number) => `${x > 0 ? '+' : ''}${String(x).replace('.', ',')}`;

export function DelaysPanel({ view }: { view: ScheduleView }) {
  const primary = view.baselines.find((b) => b.isPrimary) ?? view.baselines[0];
  const [picked, setBl] = useState(primary?.id ?? '');
  // Выбранный базовый план удалили — берём основной
  const bl = view.baselines.some((x) => x.id === picked) ? picked : (primary?.id ?? '');
  const q = useQuery({
    queryKey: ['schedule-delays', view.schedule.id, bl, view.schedule.revision],
    queryFn: () => api<Delays>(`/schedules/${view.schedule.id}/delays`, { query: { baseline: bl } }),
    enabled: Boolean(bl),
  });
  if (!view.baselines.length) return null;
  const r = q.data;
  return (
    <Panel title="Анализ задержек" meta="какие работы сдвинули окончание">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <LField label="Против базового плана">
          <Select value={bl} onChange={(e) => setBl(e.target.value)} className="max-md:h-11">
            {view.baselines.map((x) => (
              <option key={x.id} value={x.id}>
                БП-{x.number}: {x.name}
              </option>
            ))}
          </Select>
        </LField>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : r ? (
        <>
          <p className="mb-3 text-[14px]">
            Окончание: {fmtMoment(r.finishBaseline)} → {fmtMoment(r.finishCurrent)}
            <b className={clsx('ml-2', r.totalDays > 0 ? 'text-danger' : 'text-success')}>
              {n(r.totalDays)} раб. дн
            </b>
            <span className="ml-3 text-ink-2">проверено изменённых работ: {r.analysed}</span>
          </p>
          {r.items.length ? (
            <ul className="mb-3 divide-y divide-line text-[14px]">
              {r.items.map((x) => (
                <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0">
                    <span className="font-medium">
                      {x.code} {x.name}
                    </span>
                    <span className="block text-[12px] text-ink-2">{x.reasons.join('; ')}</span>
                  </span>
                  <span className="num text-danger">
                    без неё раньше на {String(x.impactDays).replace('.', ',')} раб. дн
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Задержек, сдвинувших окончание, нет">
              Изменения против базового плана не влияют на окончание проекта.
            </EmptyState>
          )}
          <p className="mb-2 text-[12px] text-ink-2">
            Вклад — на сколько раньше закончился бы проект, если бы у работы остались значения базового плана.
            Параллельные задержки перекрываются, поэтому сумма вкладов может быть больше общего переноса.
          </p>
          {r.windows.length ? (
            <details className="text-[13px]">
              <summary className="min-h-11 cursor-pointer py-2">Окна: сдвиг окончания между версиями</summary>
              <ul className="divide-y divide-line">
                {r.windows.map((w) => (
                  <li key={`${w.from}|${w.to}`} className="flex flex-wrap justify-between gap-2 py-1.5">
                    <span>
                      {w.from} → {w.to}
                    </span>
                    <span className={clsx('num', w.shiftDays > 0 ? 'text-danger' : 'text-ink-2')}>
                      {fmtMoment(w.fromFinish)} → {fmtMoment(w.toFinish)} ({n(w.shiftDays)} раб. дн)
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}
